import { reatomAsync, withDataAtom, withErrorAtom } from "@reatom/async";
import { atom } from "@reatom/framework";
import {
  ProductSourceDto,
  ProductSourceDtoSourceEnum,
} from "definitions/product-storage";
import { API } from "src/api";
import { withIsLoadingAtom } from "src/common/pipes";
import { fetchProductDetail } from "src/store/fetchers/getProductDetailRequest";

import {
  getCachedProductPrice,
  ProductPriceResponse,
  setCachedProductPrice,
} from "../idbs/productPriceIndexedDb";
import { loadShihuoProductFull } from "./loadShihuoProductFull";

export const getProductDetailFromStoragePage = reatomAsync(
  async (_ctx, productId: number) => fetchProductDetail(productId),
  "getProductDetailFromStoragePage",
).pipe(withDataAtom(null), withIsLoadingAtom());

type LoadProductArgs = {
  spuId: string;
  platform: ProductSourceDtoSourceEnum;
  force: boolean;
};

type ProductPriceLoader = (
  spuId: string,
) => Promise<ProductPriceResponse | undefined>;

const PRICE_LOADERS: Record<
  ProductSourceDtoSourceEnum,
  ProductPriceLoader | null
> = {
  [ProductSourceDtoSourceEnum.Poizon]: (spuId) =>
    API.poizonProductApi
      .poizonApiControllerGetProductInfo(Number(spuId))
      .then((res) => res.data),

  [ProductSourceDtoSourceEnum.Dewu]: null,

  [ProductSourceDtoSourceEnum.Goat]: (spuId) =>
    API.goatApi.goatGetBuyBarData(spuId).then((res) => res.data),

  [ProductSourceDtoSourceEnum.Shihuo]: loadShihuoProductFull,
};

export const PRICE_LOADABLE_SOURCES = Object.values(
  ProductSourceDtoSourceEnum,
).filter((source) => PRICE_LOADERS[source] !== null);

export const priceCacheKey = (
  platform: ProductSourceDtoSourceEnum,
  spuId: string,
) => `${platform}:${spuId}`;

export const priceLoadedAtAtom = atom<number | null>(null, "priceLoadedAt");

export const productPriceAtom = atom<ProductPriceResponse | null>(
  null,
  "productPrice",
);

export const loadProductPrice = reatomAsync(
  async (ctx, { spuId, platform, force }: LoadProductArgs) => {
    productPriceAtom(ctx, null);
    priceLoadedAtAtom(ctx, null);

    const key = priceCacheKey(platform, spuId);

    if (!force) {
      const cached = await getCachedProductPrice(key);

      if (cached) {
        productPriceAtom(ctx, cached.data);
        priceLoadedAtAtom(ctx, cached.cachedAt);
        return;
      }
    }

    const data = await PRICE_LOADERS[platform]?.(spuId);

    if (data) await setCachedProductPrice(key, data);

    productPriceAtom(ctx, data ?? null);
    priceLoadedAtAtom(ctx, data ? Date.now() : null);
  },
  "loadProductPrice",
).pipe(
  withIsLoadingAtom(),
  withErrorAtom(undefined, { resetTrigger: "onEffect" }),
);

export const findCachedPlatform = async (
  sources: ProductSourceDto[] | undefined,
): Promise<ProductSourceDtoSourceEnum | null> => {
  let freshest: {
    platform: ProductSourceDtoSourceEnum;
    cachedAt: number;
  } | null = null;

  for (const platform of PRICE_LOADABLE_SOURCES) {
    const externalId = sources?.find(
      (source) => source.source === platform,
    )?.externalId;

    if (!externalId) continue;

    const cached = await getCachedProductPrice(
      priceCacheKey(platform, externalId),
    );

    if (!cached) continue;

    if (!freshest || cached.cachedAt > freshest.cachedAt) {
      freshest = { platform, cachedAt: cached.cachedAt };
    }
  }

  return freshest?.platform ?? null;
};
